"use client";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { routes } from "@/config/routes";
import toast from "react-hot-toast";
import useApi from "@/hooks/useApi";
import { IResponseData } from "@/utilities/types/requests";
import {
  ICreatePackageDTO,
  IPackage,
  IUpdatePackageDTO,
} from "@/utilities/types/Package";
import PackageRepository from "@/utilities/repositories/Package";
import PackageForm from "./components/package-form";

export default function CreateEditPackage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState<IPackage | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { request: getPackage } = useApi<IResponseData<IPackage>>({
    request: PackageRepository.getPackage,
  });

  const { request: createPackage, loading: creatingPackage } = useApi({
    request: PackageRepository.createPackage,
  });

  const { request: updatePackage, loading: updatingPackage } = useApi({
    request: PackageRepository.updatePackage,
  });

  const isEditMode = !!id && id !== ":id";

  useEffect(() => {
    if (isEditMode) {
      fetchPackageDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPackageDetails = async () => {
    try {
      setIsLoading(true);
      const response = await getPackage(id as string);
      if (response?.data) {
        setInitialData(response.data);
      }
    } catch (error) {
      console.error("Error fetching package details", error);
      toast.error("Failed to fetch package details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (data: ICreatePackageDTO) => {
    try {
      if (isEditMode && id) {
        const updateData: IUpdatePackageDTO = {
          name: data.name,
          description: data.description,
          price: data.price,
          numberOfCredit: data.numberOfCredit,
          duration: data.duration,
          discount: data.discount,
          status: data.status,
          isPopular: data.isPopular,
          isHidden: data.isHidden,
          durationType: data.durationType,
        };

        await updatePackage(id, updateData);
        toast.success("Package updated successfully");
      } else {
        await createPackage(data);
        toast.success("Package created successfully");
      }

      navigate(routes.package.listing);
    } catch (error) {
      console.error("Error saving package", error);
      toast.error(
        isEditMode ? "Failed to update package" : "Failed to create package",
      );
      throw error;
    }
  };

  return (
    <div className="@container">
      <PackageForm
        initialData={initialData}
        isLoading={isLoading || creatingPackage || updatingPackage}
        onSubmit={handleSubmit}
        isUpdate={isEditMode}
      />
    </div>
  );
}
